import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { Trans, useTranslation } from "react-i18next";
import PrimaryButton from "../../Button/PrimaryButton/PrimaryButton";
import { HeroAction, HeroSlideConfig } from "../../../constants/landingSections";
import { CmsHeroSlide } from "../../../types/cms.types";
import { pickCmsText, renderHighlight } from "../../../utils/cmsText";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

type HeroCarouselProps = {
    styles: Record<string, string>;
    slides: HeroSlideConfig[];
    onAction: (action: HeroAction) => void;
    buttonDisabled?: boolean;
    cmsSlides?: CmsHeroSlide[]; // Published CMS banners; built-in slides are the fallback
};

const HeroCarousel = ({
    styles,
    slides,
    onAction,
    buttonDisabled = false,
    cmsSlides,
}: HeroCarouselProps) => {
    const { t, i18n } = useTranslation();

    // Any published CMS banner replaces the whole built-in carousel
    const useCms = (cmsSlides?.length ?? 0) > 0;
    const defaultBackground = slides[0]?.backgroundImage;

    const handleCmsAction = (slide: CmsHeroSlide) => {
        if (slide.action === "link") {
            if (slide.url) window.open(slide.url, "_blank", "noopener,noreferrer");
            return;
        }
        onAction(slide.action === "onboarding" ? "onboarding" : "about");
    };

    return (
        <div className={styles.Hero}>
            <div className={styles.CarouselWrapper}>
                <Swiper
                    modules={[Navigation, Pagination]}
                    spaceBetween={20}
                    slidesPerView={1}
                    pagination={{ clickable: true }}
                    loop
                    className={`${styles.Carousel} HeroSwiper`}
                >
                    {useCms
                        ? cmsSlides!.map((slide) => {
                              const buttonText = pickCmsText(slide.button, i18n.language, "");
                              return (
                                  <SwiperSlide key={slide._key}>
                                      <div className={styles.SlideBgOverlay}></div>
                                      <div className={styles.Slide}>
                                          <div
                                              className={styles.SlideBgImg}
                                              style={{ backgroundImage: `url(${slide.imageUrl || defaultBackground})` }}
                                          >
                                              {slide.avatarUrl && (
                                                  <div className={styles.Mascots}>
                                                      <img src={slide.avatarUrl} alt="" />
                                                  </div>
                                              )}
                                          </div>
                                          <div className={styles.SlideContent}>
                                              <div className={styles.SlideTitle}>
                                                  <p>{renderHighlight(pickCmsText(slide.title, i18n.language, ""))}</p>
                                              </div>
                                              <div className={styles.SlideText}>
                                                  <p>{pickCmsText(slide.text, i18n.language, "")}</p>
                                              </div>
                                              {buttonText !== "" && (
                                                  <PrimaryButton
                                                      text={buttonText}
                                                      onClick={() => handleCmsAction(slide)}
                                                      disabled={buttonDisabled}
                                                  />
                                              )}
                                          </div>
                                      </div>
                                  </SwiperSlide>
                              );
                          })
                        : slides.map((slide) => (
                              <SwiperSlide key={slide.id}>
                                  <div className={styles.SlideBgOverlay}></div>
                                  <div className={styles.Slide}>
                                      <div
                                          className={styles.SlideBgImg}
                                          style={{ backgroundImage: `url(${slide.backgroundImage})` }}
                                      >
                                          <div className={styles[slide.mascotClassName]}>
                                              <img src={slide.mascotImage} alt={slide.mascotAlt} />
                                          </div>
                                      </div>
                                      <div className={styles.SlideContent}>
                                          <div className={styles.SlideTitle}>
                                              <p>
                                                  <Trans i18nKey={slide.titleKey} components={{ 1: <span />, 4: <br /> }} />
                                              </p>
                                          </div>
                                          <div className={styles.SlideText}>
                                              <p>{t(slide.textKey)}</p>
                                          </div>
                                          <PrimaryButton
                                              text={t(slide.buttonKey)}
                                              onClick={() => onAction(slide.action)}
                                              disabled={buttonDisabled}
                                          />
                                      </div>
                                  </div>
                              </SwiperSlide>
                          ))}
                </Swiper>
            </div>
        </div>
    );
};

export default HeroCarousel;
